/**
 * Utility functions for handling placeholder/forward-referenced stages
 * in the Approval Workflow Wizard
 */

import type { Workflow, WorkflowStage, StageData, Rule } from './types';

export interface PlaceholderStageReference {
  referencedStageName: string;
  referencedStageId: string;
  referencedFrom: {
    stageId: string;
    stageName: string;
    location: 'rule' | 'onApproved' | 'onRejected';
    ruleId?: string;
    ruleName?: string;
  }[];
}

/**
 * Checks if a stage ID is a placeholder (doesn't exist in the workflow)
 */
export function isPlaceholderStage(stageId: string, workflow: Workflow): boolean {
  if (!stageId || stageId === '__create_new__') return false;
  return !workflow.stages.some(s => s.id === stageId);
}

/**
 * Finds all placeholder stage references in a workflow
 */
export function findPlaceholderStages(workflow: Workflow): PlaceholderStageReference[] {
  const placeholders = new Map<string, PlaceholderStageReference>();

  workflow.stages.forEach(stage => {
    if (!stage.stageData) return;

    const { smartRules } = stage.stageData;

    // Check rules for placeholder references
    smartRules.rules?.forEach(rule => {
      checkRuleForPlaceholders(rule, stage, workflow, placeholders);
    });

    // Check onApproved action
    if (smartRules.onApproved?.type === 'gotoStage' && smartRules.onApproved.targetStageId) {
      const targetId = smartRules.onApproved.targetStageId;
      if (isPlaceholderStage(targetId, workflow)) {
        addPlaceholderReference(
          placeholders,
          targetId,
          smartRules.onApproved.targetStageName || 'Unnamed Stage',
          stage,
          'onApproved'
        );
      }
    }

    // Check onRejected action
    if (smartRules.onRejected?.type === 'gotoStage' && smartRules.onRejected.targetStageId) {
      const targetId = smartRules.onRejected.targetStageId;
      if (isPlaceholderStage(targetId, workflow)) {
        addPlaceholderReference(
          placeholders,
          targetId,
          smartRules.onRejected.targetStageName || 'Unnamed Stage',
          stage,
          'onRejected'
        );
      }
    }
  });

  return Array.from(placeholders.values());
}

/**
 * Checks a rule for placeholder stage references
 */
function checkRuleForPlaceholders(
  rule: Rule,
  stage: WorkflowStage,
  workflow: Workflow,
  placeholders: Map<string, PlaceholderStageReference>
) {
  // Check ifTrue action
  if (rule.ifTrue?.action === 'gotoStage' && rule.ifTrue.targetStageId) {
    const targetId = rule.ifTrue.targetStageId;
    if (isPlaceholderStage(targetId, workflow)) {
      addPlaceholderReference(
        placeholders,
        targetId,
        rule.ifTrue.targetStageName || 'Unnamed Stage',
        stage,
        'rule',
        rule.id,
        rule.name
      );
    }
  }

  // Check ifFalse action
  if (rule.ifFalse?.action === 'gotoStage' && rule.ifFalse.targetStageId) {
    const targetId = rule.ifFalse.targetStageId;
    if (isPlaceholderStage(targetId, workflow)) {
      addPlaceholderReference(
        placeholders,
        targetId,
        rule.ifFalse.targetStageName || 'Unnamed Stage',
        stage,
        'rule',
        rule.id,
        rule.name
      );
    }
  }
}

/**
 * Adds a placeholder reference to the map
 */
function addPlaceholderReference(
  placeholders: Map<string, PlaceholderStageReference>,
  stageId: string,
  stageName: string,
  fromStage: WorkflowStage,
  location: 'rule' | 'onApproved' | 'onRejected',
  ruleId?: string,
  ruleName?: string
) {
  if (!placeholders.has(stageId)) {
    placeholders.set(stageId, {
      referencedStageName: stageName,
      referencedStageId: stageId,
      referencedFrom: [],
    });
  }

  const placeholder = placeholders.get(stageId)!;
  placeholder.referencedFrom.push({
    stageId: fromStage.id,
    stageName: fromStage.name,
    location,
    ruleId,
    ruleName,
  });
}

/**
 * Validates if a workflow is ready to be activated
 * Returns an array of validation errors
 */
export function validateWorkflowForActivation(workflow: Workflow): string[] {
  const errors: string[] = [];
  const placeholders = findPlaceholderStages(workflow);

  if (placeholders.length > 0) {
    placeholders.forEach(placeholder => {
      errors.push(
        `Stage "${placeholder.referencedStageName}" is referenced but not configured. ` +
        `Referenced from: ${placeholder.referencedFrom.map(ref => ref.stageName).join(', ')}`
      );
    });
  }

  // Check if all stages have at least basic configuration
  workflow.stages.forEach(stage => {
    if (!stage.stageData || stage.status === 'draft') {
      errors.push(`Stage "${stage.name}" is not fully configured`);
    }
  });

  return errors;
}

/**
 * Generates a unique stage ID for placeholder stages
 */
export function generatePlaceholderStageId(stageName: string): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 7);
  const sanitized = stageName.toLowerCase().replace(/[^a-z0-9]/g, '-');
  return `placeholder-${sanitized}-${timestamp}-${random}`;
}
